// @ts-nocheck
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { alpha, Box, Stack, styled, Typography, useTheme } from '@mui/material';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';

import {
  DMS_MAX_ALLOWED_FILES_COUNT,
  EMPTY_ARRAY,
  EMPTY_OBJECT,
  ENTITY_DOCUMENTS_ALLOWED_FILE_EXTENSIONS,
} from '../../../../const/CommonConst';
import REGEX from '../../../../const/Regex';
import {
  downloadBlobResponse,
  getErrorHelperText,
} from '../../../../utils/Utils';
import { useUploadDocumentContext } from '../../../../context/DocumentUpload/UploadDocumentContext';
import SwalToast from '../../swalToast';
import { ReactComponent as ArrowUpIcon } from '../../../../assets/Icons/lucideIcons/arrow-up.svg';
import {
  useDocumentTagsQuery,
  useDeleteDocumentMutation,
  useDownloadDocumentMutation,
} from '../../../../features/document/api/documentInventoryQueries';
import useAttributeDocumentPreview from '../../../../hooks/useAttributeDocumentPreview';
import DocumentPreviewModal from '../../../../features/document/components/DocumentPreviewModal';
import DeleteModal from '../../Modals/DeleteModal';
import useModalDisclosure from '../../../../hooks/useModalDisclosure';
import { useDocumentCleanup } from '../../../../context/DocumentCleanupContext';
import useAttribute from '../../../../hooks/ModelInventory/useAttribute';
import ReactMuiTableListView from '../../ReactMuiTableListViewV8';
import { useTableMaxHeight } from '../../../../hooks/Configuration/useTableMaxHeight';
import {
  ActionsCell,
  extractDocumentId,
  FileNameCell,
  StatusCell,
  TagCell,
} from './MultiDocumentTableCells';

const DropzoneBox = styled(Box)(({ theme }) => ({
  border: `1.5px dashed ${
    theme.palette.primary?.light
      ? alpha(theme.palette.primary.main, 0.35)
      : theme.palette.divider
  }`,
  borderRadius: '4px',
  padding: '18px 20px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  boxSizing: 'border-box',
  backgroundColor: 'transparent',
  cursor: 'pointer',
  transition: 'border-color 0.2s ease, background-color 0.2s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.02),
  },
}));

const allTypeOfFiles = Object.values(ENTITY_DOCUMENTS_ALLOWED_FILE_EXTENSIONS);

const getBaseFileName = (fileName = '') =>
  fileName.replace(REGEX.REMOVE_FILE_EXTENSION, '');

const getFileExtension = (fileOrDoc) => {
  if (typeof fileOrDoc === 'string') {
    const parts = fileOrDoc.split('.');
    return parts.length > 1 ? parts.pop() : '';
  }
  return (
    fileOrDoc?.file_type ||
    fileOrDoc?.document_type ||
    fileOrDoc?.name?.split('.').pop() ||
    fileOrDoc?.file_name?.split('.').pop() ||
    ''
  );
};

/**
 * MultiDocumentFieldComponent - Compact table-based multi-document attribute editor.
 */
const MultiDocumentFieldComponent = ({
  value = [],
  onChange,
  label: _label,
  entityDetailsFormikForm,
  attribute,
  entityData = EMPTY_OBJECT,
  mode = 'edit',
  onFileStatusChange,
  configColumn = false,
}) => {
  const theme = useTheme();
  const { uploadFileForAttribute } = useAttribute();
  const { mutateAsync: deleteDocument } = useDeleteDocumentMutation();
  const { mutateAsync: downloadDocument } = useDownloadDocumentMutation();

  const entityId = entityData?.entity_id || entityData?.entityId;
  const entityType = entityData?.entity_type || entityData?.entityType;

  // DocumentCleanupContext: allows parent dialogs to trigger cleanup on cancel
  const documentCleanup = useDocumentCleanup();

  const { data: allTags = EMPTY_ARRAY } = useDocumentTagsQuery({
    options: { select: (data) => data?.tags },
  });

  const {
    handleFileSelection,
    handleCompleteUpload,
    handleStopUpload,
    addBatch,
    activeBatches = EMPTY_ARRAY,
    uploadProgress = EMPTY_OBJECT,
    uploadErrors = EMPTY_OBJECT,
    clearUploadError,
  } = useUploadDocumentContext();

  // Track document_ids uploaded during this component's lifecycle.
  // Used for cleanup on cancel and for delete action.
  const uploadedDocIdsRef = useRef(new Set());
  // Track upload keys for documents uploaded in this session
  const sessionKeysRef = useRef(new Set());
  const isCancelledRef = useRef(false);

  /**
   * Transforms a raw document object (from Formik form state or active background upload batches)
   * into a standardized table row structure, while preserving in-flight session metadata
   * and tracking newly assigned document IDs for cancel/cleanup.
   */
  const formatDocumentRow = useCallback((doc = {}, prevList = []) => {
    const {
      document_id,
      document_name = '',
      file_type,
      tags = [],
      key = null,
      file = null,
      error = null,
    } = doc;

    const docId = extractDocumentId(document_id ?? doc);
    const ext =
      file_type || doc.document_type || getFileExtension(document_name);
    const name = getBaseFileName(document_name);

    const existing = prevList.find(
      (p) => (docId && p.document_id === docId) || (key && p.key === key)
    );

    if (
      docId &&
      existing?.key &&
      sessionKeysRef.current.has(existing.key) &&
      !existing?.document_id
    ) {
      uploadedDocIdsRef.current.add(String(docId));
    }

    return {
      id: docId
        ? String(docId)
        : key || existing?.id || `doc-${docId || Math.random()}`,
      document_id: docId || existing?.document_id || null,
      key: key || existing?.key || null,
      file: file || existing?.file || null,
      file_name:
        file?.name ||
        doc.file_name ||
        existing?.file_name ||
        (name ? `${name}${ext ? `.${ext}` : ''}` : 'Document'),
      document_name: name,
      document_type: ext,
      file_type: ext,
      tags: tags.length > 0 ? tags : existing?.tags || [],
      status: docId || existing?.document_id ? 'Verified' : 'uploading',
      error: error || existing?.error || null,
    };
  }, []);

  /**
   * Combines persisted documents from Formik form state (`value`) with active in-flight
   * background upload batches (`activeBatches`) belonging to this attribute and entity.
   *
   * Ensures that documents currently uploading in the background remain visible in real-time
   * with live progress, while preventing duplicate entries when uploads transition to persisted.
   */
  const buildUnifiedDocumentList = useCallback(
    (valueList = [], activeBatchesList = [], prevList = []) => {
      const list = (Array.isArray(valueList) ? valueList : []).map((d) =>
        formatDocumentRow(d, prevList)
      );
      const seenKeys = new Set(list.map((d) => d.key).filter(Boolean));

      const currentEntityId = String(entityId || '');

      activeBatchesList.forEach((batch) => {
        if (currentEntityId && String(batch.entityId) !== currentEntityId) {
          return;
        }

        (batch.data || []).forEach((fileMeta) => {
          if (
            Number(fileMeta.attribute_id) !== Number(attribute?.id) ||
            !fileMeta.document_from_attribute
          ) {
            return;
          }

          if (seenKeys.has(fileMeta.key)) {
            return;
          }

          seenKeys.add(fileMeta.key);
          list.push(formatDocumentRow(fileMeta, prevList));
        });
      });

      return list;
    },
    [attribute?.id, entityId, formatDocumentRow]
  );

  // Unified document list
  const [documentsList, setDocumentsList] = useState(() =>
    buildUnifiedDocumentList(value, activeBatches, [])
  );
  const documentsListRef = useRef(documentsList);
  useEffect(() => {
    documentsListRef.current = documentsList;
  }, [documentsList]);

  // Synchronize incoming value and activeBatches updates
  useEffect(() => {
    setDocumentsList((prevList) => {
      const nextList = buildUnifiedDocumentList(value, activeBatches, prevList);
      documentsListRef.current = nextList;
      return nextList;
    });
  }, [value, activeBatches, buildUnifiedDocumentList]);

  const isDisabled = !attribute?.is_editable || mode === 'view';
  const maxLimitMB = attribute?.document_size_limit || 350;
  const maxDocuments =
    attribute?.max_documents || DMS_MAX_ALLOWED_FILES_COUNT || 10;

  const accept = useMemo(() => {
    if (
      !attribute?.supported_file_types ||
      attribute.supported_file_types.length === 0
    ) {
      return Object.fromEntries(
        allTypeOfFiles.map((fileType) => [
          fileType.mimetype,
          [`.${fileType.extension}`],
        ])
      );
    }
    const acc = {};
    attribute.supported_file_types.forEach((type) => {
      if (type?.value) {
        acc[`.${String(type?.value).toLowerCase()}`] = [];
      }
    });
    return Object.keys(acc).length > 0 ? acc : undefined;
  }, [attribute?.supported_file_types]);

  const helperText = getErrorHelperText(
    entityDetailsFormikForm?.errors?.[attribute?.id],
    entityDetailsFormikForm?.touched?.[attribute?.id]
  );
  const hasError = Boolean(helperText);

  const hasUploadingFiles = documentsList.some((doc) => {
    const hasErr =
      Boolean(doc.error) || Boolean(doc.key && uploadErrors[doc.key]);
    if (hasErr) return false;
    return (doc.file && !doc.document_id) || doc.status === 'uploading';
  });

  const hasFailedFiles = documentsList.some(
    (doc) => Boolean(doc.error) || Boolean(doc.key && uploadErrors[doc.key])
  );

  useEffect(() => {
    onFileStatusChange?.(hasUploadingFiles || hasFailedFiles || hasError);
  }, [hasUploadingFiles, hasFailedFiles, hasError, onFileStatusChange]);

  useEffect(() => {
    if (hasFailedFiles) {
      entityDetailsFormikForm?.setFieldError(
        attribute?.id,
        'Some files failed to upload. Please remove failed files.'
      );
    } else if (
      entityDetailsFormikForm?.errors?.[attribute?.id] ===
      'Some files failed to upload. Please remove failed files.'
    ) {
      entityDetailsFormikForm?.setFieldError(attribute?.id, undefined);
    }
  }, [
    hasUploadingFiles,
    hasFailedFiles,
    entityDetailsFormikForm,
    attribute?.id,
  ]);

  const syncParent = useCallback(
    (updatedList) => {
      const payloadList = updatedList.map((doc) => ({
        document_id: doc.document_id,
        document_name: doc.document_name,
        document_type: doc.document_type,
        file_name: doc.file_name,
        tags: doc.tags || [],
        key: doc.key,
        status: doc.status,
        error: doc.error,
      }));
      onChange?.(payloadList);
    },
    [onChange]
  );

  const markTouched = useCallback(() => {
    if (!attribute?.id) return;
    entityDetailsFormikForm?.setFieldTouched(attribute.id, true, false);
  }, [entityDetailsFormikForm, attribute?.id]);

  const handleDropAccepted = useCallback(
    (newFiles) => {
      markTouched();
      if (!newFiles || newFiles.length === 0) return;

      const emptyFiles = newFiles.filter((f) => f.size === 0);
      if (emptyFiles.length > 0) {
        const names = emptyFiles.map((f) => f.name).join(', ');
        SwalToast({
          icon: 'error',
          title: `${names} is empty. Please upload a valid file.`,
        });
      }

      const validFilesRaw = newFiles.filter((f) => f.size > 0);
      if (validFilesRaw.length === 0) return;

      const existingNames = new Set(
        documentsList.map((d) => d.file_name?.toLowerCase())
      );

      const duplicates = [];
      const nonDuplicates = [];

      validFilesRaw.forEach((file) => {
        const fileNameLower = file.name.toLowerCase();
        if (existingNames.has(fileNameLower)) {
          duplicates.push(file.name);
        } else {
          existingNames.add(fileNameLower);
          nonDuplicates.push(file);
        }
      });

      if (duplicates.length > 0) {
        SwalToast({
          icon: 'warning',
          title: `${duplicates.join(', ')} already added`,
        });
      }

      if (nonDuplicates.length === 0) return;

      const totalCount = documentsList.length + nonDuplicates.length;
      if (totalCount > maxDocuments) {
        SwalToast({
          icon: 'error',
          title: `You can upload a maximum of ${maxDocuments} files`,
        });
        return;
      }

      const oversizedFiles = nonDuplicates.filter(
        (file) => file.size > maxLimitMB * 1024 * 1024
      );
      if (oversizedFiles.length > 0) {
        SwalToast({
          icon: 'error',
          title: `Some files exceed the ${maxLimitMB}MB limit`,
        });
        return;
      }

      const newDocEntries = nonDuplicates.map((file) => {
        const key = crypto.randomUUID();
        const ext = getFileExtension(file.name);
        const nameWithoutExt = getBaseFileName(file.name);
        sessionKeysRef.current.add(key);
        return {
          id: key,
          key,
          file,
          document_id: null,
          document_name: nameWithoutExt,
          document_type: ext,
          file_name: file.name,
          file_size: file.size,
          tags: [],
          status: 'uploading',
          error: null,
        };
      });

      isCancelledRef.current = false;
      const updatedList = [...documentsListRef.current, ...newDocEntries];
      documentsListRef.current = updatedList;
      setDocumentsList(updatedList);
      syncParent(updatedList);

      if (configColumn) {
        newDocEntries.forEach((entry) => {
          const formData = new FormData();
          formData.append('file', entry.file);
          formData.append(
            'data',
            JSON.stringify({
              entity_id: entityId,
              entity_type: entityType,
              attribute_id: attribute?.id,
              tags: entry.tags || [],
            })
          );

          uploadFileForAttribute(formData)
            .then((res) => {
              if (isCancelledRef.current) {
                if (res?.data?.document_id) {
                  deleteDocument({ document_id: res.data.document_id });
                }
                return;
              }
              const data = res?.data;
              if (data) {
                if (data.document_id) {
                  uploadedDocIdsRef.current.add(String(data.document_id));
                }
                const nextList = documentsListRef.current.map((doc) => {
                  if (doc.key === entry.key) {
                    return {
                      ...doc,
                      document_id: data.document_id,
                      document_name: data.document_name || doc.document_name,
                      document_type: data.file_type || doc.document_type,
                      file_type: data.file_type || doc.document_type,
                      tags: doc.tags ?? data.tags ?? [],
                      status: 'Verified',
                      error: null,
                    };
                  }
                  return doc;
                });
                documentsListRef.current = nextList;
                setDocumentsList(nextList);
                syncParent(nextList);
              }
            })
            .catch((err) => {
              if (isCancelledRef.current) return;
              const nextList = documentsListRef.current.map((doc) => {
                if (doc.key === entry.key) {
                  return {
                    ...doc,
                    status: 'failed',
                    error: err?.message || 'Upload failed. Please retry.',
                  };
                }
                return doc;
              });
              documentsListRef.current = nextList;
              setDocumentsList(nextList);
              syncParent(nextList);
            });
        });
        return;
      }

      const batchId = crypto.randomUUID();
      const batchData = {
        entityId,
        entityType,
        requestType: entityData?.request_type,
        uniqueId: entityData?.unique_id,
      };

      const filesMetadata = newDocEntries.map((entry) => ({
        file: entry.file,
        key: entry.key,
        document_name: entry.file.name,
        attribute_id: attribute?.id,
        document_from_attribute: true,
        entities: [],
        tags: entry.tags || [],
        fileSize: entry.file_size,
      }));

      addBatch(batchId, batchData, filesMetadata);
      handleFileSelection(filesMetadata);
      handleCompleteUpload(batchId, filesMetadata, batchData);
    },
    [
      markTouched,
      documentsList,
      maxDocuments,
      maxLimitMB,
      attribute?.id,
      entityId,
      entityType,
      entityData?.request_type,
      entityData?.unique_id,
      syncParent,
      configColumn,
      uploadFileForAttribute,
      addBatch,
      handleFileSelection,
      handleCompleteUpload,
      deleteDocument,
      setDocumentsList,
    ]
  );

  const handleDropRejected = useCallback(
    (fileRejections) => {
      markTouched();
      if (!fileRejections || fileRejections.length === 0) return;

      const hasTooManyFiles = fileRejections.some((rejection) =>
        rejection?.errors?.some((err) => err?.code === 'too-many-files')
      );
      if (hasTooManyFiles) {
        SwalToast({
          icon: 'warning',
          title: `You can upload a maximum of ${maxDocuments} file${maxDocuments > 1 ? 's' : ''}`,
        });
        return;
      }

      const largeFiles = fileRejections.filter((rejection) =>
        rejection?.errors?.some((err) => err?.code === 'file-too-large')
      );
      if (largeFiles.length > 0) {
        SwalToast({
          icon: 'warning',
          title: `File size exceeds ${maxLimitMB}MB limit`,
        });
        return;
      }

      const invalidTypeFiles = fileRejections.filter((rejection) =>
        rejection?.errors?.some((err) => err?.code === 'file-invalid-type')
      );
      const targetRejections =
        invalidTypeFiles.length > 0 ? invalidTypeFiles : fileRejections;

      const rejectedExtensions = [
        ...new Set(
          targetRejections
            .map((rejection) => rejection?.file?.name?.split('.')?.pop() || '')
            .filter(Boolean)
        ),
      ];

      if (rejectedExtensions.length > 0) {
        const verb = rejectedExtensions.length > 1 ? 'are' : 'is';
        SwalToast({
          icon: 'warning',
          title: `.${rejectedExtensions.join(', ')} file type ${verb} not supported`,
        });
      }
    },
    [markTouched, maxDocuments, maxLimitMB]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: true,
    maxFiles: maxDocuments,
    maxSize: maxLimitMB * 1024 * 1024,
    accept,
    disabled: isDisabled,
    onDropAccepted: handleDropAccepted,
    onDropRejected: handleDropRejected,
  });

  const handleRemoveFileByIndex = useCallback(
    (toRemoveIndex) => {
      const docToRemove = documentsListRef.current[toRemoveIndex];
      if (!docToRemove) return;

      if (docToRemove.key) {
        handleStopUpload(docToRemove.key);
        clearUploadError(docToRemove.key);
        sessionKeysRef.current.delete(docToRemove.key);
      }

      // Delete from server if this document was uploaded during this session
      const docId = extractDocumentId(docToRemove);
      const docIdStr = docId != null ? String(docId) : null;
      const isSessionUpload =
        (docIdStr &&
          (uploadedDocIdsRef.current.has(docIdStr) ||
            uploadedDocIdsRef.current.has(docId))) ||
        Boolean(docToRemove.file && docId);

      if (docId && isSessionUpload) {
        deleteDocument({ document_id: docId });
        uploadedDocIdsRef.current.delete(docId);
        if (docIdStr) uploadedDocIdsRef.current.delete(docIdStr);
      }

      const updatedList = documentsListRef.current.filter(
        (_, idx) => idx !== toRemoveIndex
      );
      documentsListRef.current = updatedList;
      setDocumentsList(updatedList);
      syncParent(updatedList);
    },
    [handleStopUpload, clearUploadError, deleteDocument, syncParent]
  );

  const {
    open: isDeleteModalOpen,
    handleOpen: openDeleteModal,
    handleClose: closeDeleteModal,
  } = useModalDisclosure(false);
  const [docToDelete, setDocToDelete] = useState(null);

  const handleRequestDelete = useCallback(
    (index, doc) => {
      const targetDoc = doc || documentsListRef.current[index];
      if (!targetDoc) return;
      setDocToDelete(targetDoc);
      openDeleteModal();
    },
    [openDeleteModal, setDocToDelete]
  );

  const handleCloseDeleteModal = useCallback(() => {
    closeDeleteModal();
    setDocToDelete(null);
  }, [closeDeleteModal, setDocToDelete]);

  const handleConfirmDelete = useCallback(() => {
    if (!docToDelete) {
      handleCloseDeleteModal();
      return;
    }
    const docId = extractDocumentId(docToDelete);
    const docKey = docToDelete?.key;

    const currentIndex = documentsListRef.current.findIndex((d) => {
      if (docId != null && extractDocumentId(d) === docId) return true;
      if (docKey && d?.key === docKey) return true;
      return d === docToDelete;
    });

    if (currentIndex !== -1) {
      handleRemoveFileByIndex(currentIndex);
    }
    handleCloseDeleteModal();
  }, [docToDelete, handleRemoveFileByIndex, handleCloseDeleteModal]);

  const handleDocTagsChange = useCallback(
    (docIndex, newTags) => {
      const updatedList = documentsListRef.current.map((doc, idx) =>
        idx === docIndex ? { ...doc, tags: newTags } : doc
      );
      documentsListRef.current = updatedList;
      setDocumentsList(updatedList);
      syncParent(updatedList);
    },
    [syncParent]
  );

  const handleDownload = useCallback(
    (doc) => {
      const docId = extractDocumentId(doc);
      if (!docId) {
        return;
      }
      const exportType = doc.document_type || doc.file_type || '';
      downloadDocument({
        params: {
          document_id: docId,
          entity_id: entityId,
          entity_type: entityType,
          export_type: exportType,
          commit_id: 1,
        },
        config: {
          responseType: exportType === 'onedrive_docx' ? 'json' : 'blob',
          loader: false,
        },
      })
        .then((response) => {
          if (!response) return;
          if (exportType === 'onedrive_docx') {
            window.open(
              response?.data?.Data || response?.data?.path || response?.path,
              '_blank'
            );
            return;
          }
          const fileName = doc.document_name
            ? `${doc.document_name}${exportType ? `.${exportType}` : ''}`
            : doc.file_name || 'document';
          downloadBlobResponse(response, fileName);
        })
        .catch((error) => {
          console.error('Failed to download document:', error);
        });
    },
    [downloadDocument, entityId, entityType]
  );

  const { previewDocument, previewModalProps } = useAttributeDocumentPreview({
    entityId,
    entityType,
  });

  const handlePreview = useCallback(
    (doc) => {
      const docId = extractDocumentId(doc);
      if (docId) {
        const exportType = doc.document_type || doc.file_type || '';
        previewDocument(docId, {
          ...doc,
          file_type: exportType,
          document_type: exportType,
          document_id: docId,
          entity_id: entityId,
          entity_type: entityType,
        });
      }
    },
    [previewDocument, entityId, entityType]
  );

  /**
   * Cancel and cleanup: delete all documents uploaded during this session
   * from the server. Called on cancel by MultiEditModal or ConfigColumnRowDialog.
   */
  const handleCancelAndCleanup = useCallback(async () => {
    isCancelledRef.current = true;
    const promises = [];
    // Stop any in-progress uploads
    sessionKeysRef.current.forEach((key) => {
      handleStopUpload(key);
      clearUploadError(key);
    });
    // Delete completed uploads from server (deduplicated)
    const idsToDelete = Array.from(
      new Set(Array.from(uploadedDocIdsRef.current).map(String).filter(Boolean))
    );
    uploadedDocIdsRef.current.clear();
    sessionKeysRef.current.clear();

    idsToDelete.forEach((docId) => {
      promises.push(deleteDocument({ document_id: docId }));
    });
    await Promise.allSettled(promises);
  }, [handleStopUpload, clearUploadError, deleteDocument]);

  // Register cleanup with DocumentCleanupContext (if provided by parent)
  useEffect(() => {
    if (!documentCleanup || !attribute?.id) return;
    const attrId = attribute.id;
    documentCleanup.registerCleanup(attrId, handleCancelAndCleanup);
    return () => {
      documentCleanup.unregisterCleanup(attrId);
    };
  }, [documentCleanup, attribute?.id, handleCancelAndCleanup]);

  const { maxTableHeight } = useTableMaxHeight({
    headerHeight: 64,
    titleHeight: mode === 'view' ? 40 : 130,
    tabsHeight: 0,
    searchBarHeight: 0,
    paginationHeight: 64,
    paddingAndMargins: mode === 'view' ? 140 : 200,
    adjustedHeight: 0,
  });

  const renderFileNameCell = useCallback(
    (cellProps) => (
      <FileNameCell {...cellProps} mode={mode} onPreview={handlePreview} />
    ),
    [mode, handlePreview]
  );

  const renderTagCell = useCallback(
    (cellProps) => {
      const doc = cellProps.row?.original;
      const hasValidDocId = Boolean(
        doc?.document_id != null &&
        !String(doc.document_id).startsWith('doc-') &&
        doc.document_id !== ''
      );
      const isRowUploading = !hasValidDocId && mode !== 'view';
      return (
        <TagCell
          {...cellProps}
          mode={mode}
          allTags={allTags}
          isDisabled={isDisabled || isRowUploading}
          onDocTagsChange={handleDocTagsChange}
        />
      );
    },
    [mode, allTags, isDisabled, handleDocTagsChange]
  );

  const renderStatusCell = useCallback(
    (cellProps) => (
      <StatusCell
        {...cellProps}
        mode={mode}
        uploadErrors={uploadErrors}
        uploadProgress={uploadProgress}
      />
    ),
    [mode, uploadErrors, uploadProgress]
  );

  const renderActionsCell = useCallback(
    (cellProps) => (
      <ActionsCell
        {...cellProps}
        mode={mode}
        isDisabled={isDisabled}
        onDownload={handleDownload}
        onRemoveFileByIndex={handleRequestDelete}
      />
    ),
    [mode, isDisabled, handleDownload, handleRequestDelete]
  );

  const columns = useMemo(
    () => [
      {
        Header: 'File Name',
        accessor: 'file_name',
        id: 'file_name',
        disableSortBy: true,
        width: 320,
        minWidth: 160,
        Cell: renderFileNameCell,
      },
      {
        Header: 'Document Tag',
        accessor: 'tags',
        id: 'tags',
        disableSortBy: true,
        width: 220,
        minWidth: 150,
        Cell: renderTagCell,
      },
      {
        Header: 'Status',
        accessor: 'status',
        id: 'status',
        disableSortBy: true,
        width: 140,
        minWidth: 100,
        Cell: renderStatusCell,
      },
      {
        Header: 'Actions',
        accessor: 'actions',
        id: 'actions',
        disableSortBy: true,
        align: 'right',
        width: 80,
        minWidth: 70,
        maxWidth: 120,
        Cell: renderActionsCell,
      },
    ],
    [
      renderFileNameCell,
      renderTagCell,
      renderStatusCell,
      renderActionsCell,
      mode,
    ]
  );

  const getHeaderProps = useCallback((column) => {
    if (column?.id === 'actions') {
      return {
        style: {
          justifyContent: 'center',
        },
      };
    }
    return {};
  }, []);

  const getRowProps = useCallback(
    () => ({
      style: {
        width: '100%',
        minWidth: '100%',
      },
    }),
    []
  );

  return (
    <>
      <Stack spacing={2} sx={{ width: '100%' }}>
        {/* Compact Dropzone Container with transparent background */}
        {mode !== 'view' && (
          <DropzoneBox {...getRootProps()}>
            <input aria-label="Upload Document" {...getInputProps()} />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
                width: '100%',
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  color: theme.palette.primary.main,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <ArrowUpIcon width={18} height={18} />
              </Box>
              <Typography variant="body2" fontWeight={500}>
                {isDragActive ? (
                  'Drop the files here...'
                ) : (
                  <>
                    Drag & drop files or{' '}
                    <Box
                      component="span"
                      sx={{
                        color: 'primary.main',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        fontWeight: 600,
                      }}
                    >
                      browse
                    </Box>
                  </>
                )}
              </Typography>
            </Box>
          </DropzoneBox>
        )}

        {/* Attached Documents Table Section - hidden when empty */}
        {documentsList.length > 0 && (
          <Stack spacing={2}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 0.75,
              }}
            >
              <Typography
                variant="subtitle1"
                fontWeight={600}
                sx={{ lineHeight: 1.2 }}
              >
                Attached Documents
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: 400, lineHeight: 1.2 }}
              >
                {mode === 'view'
                  ? `${documentsList.length} ${
                      documentsList.length === 1 ? 'file' : 'files'
                    }`
                  : `${documentsList.length}/${maxDocuments} files`}
              </Typography>
            </Box>

            <Box
              sx={{
                borderRadius: '4px',
                border: (t) => `1px solid ${t.palette.divider}`,
                overflow: 'hidden',
                bgcolor: 'background.paper',
                width: '100%',
                '& .MuiTable-root': {
                  width: '100% !important',
                },
                '& .MuiTableRow-root': {
                  width: '100% !important',
                },
                '& .MuiTableContainer-root': {
                  maxHeight: `${Math.max(maxTableHeight, 180)}px !important`,
                },
              }}
            >
              <ReactMuiTableListView
                data={documentsList}
                columns={columns}
                getHeaderProps={getHeaderProps}
                getRowProps={getRowProps}
                maxTableHeight={Math.max(maxTableHeight, 180)}
                enablePagination={false}
                stickyHeader
                autoFetch={false}
              />
            </Box>
          </Stack>
        )}

        {helperText && (
          <Typography variant="caption" color="error.main">
            {helperText}
          </Typography>
        )}
      </Stack>

      <DocumentPreviewModal {...previewModalProps} />

      <DeleteModal
        open={isDeleteModalOpen}
        handleClose={handleCloseDeleteModal}
        deleteConfirm={handleConfirmDelete}
        resourceName="Document"
        alertLabelText={`Are you sure you want to delete the document "${docToDelete?.file_name || docToDelete?.document_name}"?`}
        warningText=""
      />
    </>
  );
};

MultiDocumentFieldComponent.propTypes = {
  label: PropTypes.string,
  required: PropTypes.bool,
  value: PropTypes.arrayOf(
    PropTypes.shape({
      document_name: PropTypes.string,
      document_type: PropTypes.string,
      document_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      tags: PropTypes.arrayOf(
        PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})])
      ),
    })
  ),
  onChange: PropTypes.func,
  attribute: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    description: PropTypes.string,
    is_editable: PropTypes.bool,
    name: PropTypes.string,
    supported_file_types: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
      })
    ),
    document_size_limit: PropTypes.number,
    max_documents: PropTypes.number,
    input_control_type: PropTypes.string,
  }).isRequired,
  entityData: PropTypes.shape({
    entity_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    entity_type: PropTypes.string,
    unique_id: PropTypes.string,
    entityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    entityType: PropTypes.string,
    request_type: PropTypes.string,
  }),
  entityDetailsFormikForm: PropTypes.shape({
    setFieldTouched: PropTypes.func,
    setFieldValue: PropTypes.func,
    setFieldError: PropTypes.func,
    errors: PropTypes.shape({}),
    touched: PropTypes.shape({}),
  }),
  mode: PropTypes.string,
  onFileStatusChange: PropTypes.func,
  configColumn: PropTypes.bool,
};

MultiDocumentFieldComponent.defaultProps = {
  onChange: () => {},
};

export default MultiDocumentFieldComponent;
